import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation, Link, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Image, Bug, Mail, CreditCard, 
  LogOut, Menu, X, ChevronRight, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin Auth Context
const AdminAuthContext = createContext({
  isAdminAuthenticated: false,
  adminToken: null,
  adminLogin: () => {},
  adminLogout: () => {},
});

export const useAdminAuth = () => useContext(AdminAuthContext);

export const AdminAuthProvider = ({ children }) => {
  const [adminToken, setAdminToken] = useState(() => 
    localStorage.getItem('mandala-admin-token')
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (adminToken) {
        try {
          const res = await fetch(`${API}/admin/verify`, {
            headers: { Authorization: `Bearer ${adminToken}` }
          });
          if (!res.ok) {
            localStorage.removeItem('mandala-admin-token');
            setAdminToken(null);
          }
        } catch {
          localStorage.removeItem('mandala-admin-token');
          setAdminToken(null);
        }
      }
      setIsLoading(false);
    };
    verifyToken();
  }, [adminToken]);

  const adminLogin = (token) => {
    setAdminToken(token);
    localStorage.setItem('mandala-admin-token', token);
  };

  const adminLogout = () => {
    setAdminToken(null);
    localStorage.removeItem('mandala-admin-token');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AdminAuthContext.Provider value={{
      isAdminAuthenticated: !!adminToken,
      adminToken,
      adminLogin,
      adminLogout,
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

// Admin Layout with Sidebar
export const AdminLayout = () => {
  const { isAdminAuthenticated, adminLogout, adminToken } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    if (!isAdminAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAdminAuthenticated, navigate]);

  if (!isAdminAuthenticated) return null;

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/users', icon: Users, label: 'Users' },
    { path: '/admin/artworks', icon: Image, label: 'Artworks' },
    { path: '/admin/bugs', icon: Bug, label: 'Bug Reports' },
    { path: '/admin/email', icon: Mail, label: 'Email Center' },
    { path: '/admin/transactions', icon: CreditCard, label: 'Transactions' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-900 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-800 border-r border-gray-700 transition-all duration-300 flex flex-col`}>
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-700">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-emerald-500" />
              <span className="font-semibold text-white">Admin Portal</span>
            </div>
          )}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-gray-700 rounded-lg text-gray-400"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map(({ path, icon: Icon, label, exact }) => (
            <Link
              key={path}
              to={path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                isActive(path, exact)
                  ? 'bg-emerald-600 text-white'
                  : 'text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
            >
              <Icon size={20} />
              {sidebarOpen && <span>{label}</span>}
            </Link>
          ))}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-gray-700">
          <button
            onClick={() => {
              adminLogout();
              navigate('/admin/login');
            }}
            className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-gray-400 hover:bg-gray-700 hover:text-white transition-colors"
          >
            <LogOut size={20} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          <Outlet context={{ adminToken }} />
        </div>
      </main>
    </div>
  );
};

// Export API helper
export const adminFetch = async (endpoint, options = {}, token) => {
  console.log('adminFetch called:', endpoint, 'Token present:', !!token);
  
  if (!token) {
    console.error('adminFetch: No token provided!');
    throw new Error('Authentication required');
  }
  
  const fullUrl = `${API}${endpoint}`;
  console.log('adminFetch URL:', fullUrl);
  
  try {
    const res = await fetch(fullUrl, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
    
    console.log('adminFetch response status:', res.status);
    
    if (!res.ok) {
      const error = await res.json().catch(() => ({ detail: 'Request failed' }));
      console.error('adminFetch error:', error);
      throw new Error(error.detail || 'Request failed');
    }
    
    const data = await res.json();
    console.log('adminFetch success, keys:', Object.keys(data));
    return data;
  } catch (err) {
    console.error('adminFetch exception:', err.message);
    throw err;
  }
};
